/**
 * Step 1 (Event) of the Build Itinerary wizard.
 *
 * Collects the event name + date range and a schedule source (paste URL /
 * upload PDF / upload image), then runs the existing schedule-ingestion flow
 * (create -> upload/start -> poll) via useScheduleIngestionRun. On a completed
 * extraction it reports the ingestion id + the collected event details up to
 * the wizard, which advances to Step 2.
 *
 * Dates are entered as INCLUSIVE all-day dates (what the user sees on a
 * calendar). The confirm API wants an EXCLUSIVE all-day end, so we convert with
 * the shared calendarTime helper when handing details upward. Confirm/review is
 * Slice B; Slice A only carries the collected details forward.
 */

import { useEffect, useRef, useState } from 'react';
import {
  inclusiveDateToExclusiveEndIso,
  inclusiveDateToStartIso,
} from '../../../../../utils/calendarTime';
import { validateScheduleFile } from '../scheduleIngestionShared';
import { useScheduleIngestionRun } from './useScheduleIngestionRun';
import { SourceChooser, type ScheduleSource } from './SourceChooser';
import { deriveEventName } from './eventName';
import { WhatHappensNextRail } from './WhatHappensNextRail';
import { LinkImportReview } from './LinkImportReview';
import { useAuth } from '../../../../../hooks/useAuth';
import { tourAnchor } from '../../../../tours/tourAnchors';
import { useTour } from '../../../../tours/useTour';
import { BUILD_ITINERARY_FEATURE, BUILD_ITINERARY_TOURS } from './buildItineraryTours';

/** The event details Step 1 collects, handed to the wizard on completion. */
export interface EventStepResult {
  ingestionId: string;
  /**
   * The tournament the import resolves into, once the ingestion has been
   * confirmed. Steps 2-3 read the tournament's games from this id. Undefined
   * while confirm is not yet wired (Slice A stops at extraction); later slices
   * populate it so the review step can load matched games.
   */
  calendarEventId?: string;
  eventName: string;
  /** Inclusive start date, YYYY-MM-DD. Blank when the user let the schedule set it. */
  startDate: string;
  /** Inclusive end date (the last day the event spans), YYYY-MM-DD. Blank when derived. */
  endDate: string;
  /**
   * ISO UTC all-day start at midnight, derived from startDate. Undefined when the
   * user left the dates blank — the server then derives the range from the games.
   */
  startsAtUtc?: string;
  /** ISO UTC EXCLUSIVE all-day end, derived from endDate. Undefined when derived server-side. */
  endsAtUtc?: string;
}

interface EventStepProps {
  onCompleted: (result: EventStepResult) => void;
  /**
   * Seeds the tournament name. Set when RosterPilot staff opened the wizard from a program's
   * itinerary request — the name the program asked for is the name to build under, and retyping it
   * by hand is both a chore and the thing that used to break the queue row's automatic match.
   */
  defaultEventName?: string;
}

/** File accept hints, per file source. The link source uploads nothing. */
const ACCEPT_BY_SOURCE: Record<'pdf' | 'image', string> = {
  pdf: 'application/pdf,.pdf',
  image: 'image/*',
};

export function EventStep({ onCompleted, defaultEventName }: EventStepProps) {
  const [eventName, setEventName] = useState(defaultEventName ?? '');
  const [source, setSource] = useState<ScheduleSource>('pdf');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
  // A source-specific input error (an unsupported or oversized file), kept
  // separate from the date-range error so each control shows its own message.
  const [sourceError, setSourceError] = useState('');

  const [linkUrl, setLinkUrl] = useState('');
  const { isSystemAdmin } = useAuth();
  const { state, runFiles, runUrl, reset } = useScheduleIngestionRun();

  // Which source started the run currently on screen. Read by the auto-proceed
  // effect, which must not act on a link run — and `source` itself cannot answer
  // that, because the user can select a different card while a run is in flight.
  const runSourceRef = useRef<ScheduleSource | null>(null);

  const isFileSource = source === 'pdf' || source === 'image';
  // The primary action after a run resolves (continue / retry) receives focus so
  // keyboard and screen-reader users are not stranded when the long async
  // extraction finishes and the spinner is replaced.
  const resolvedActionRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (
      state.phase === 'completed' ||
      state.phase === 'failed' ||
      state.phase === 'empty'
    ) {
      resolvedActionRef.current?.focus();
    }
  }, [state.phase]);

  // Guard the date range once, in one place, so both the URL and file paths
  // share the same validation before kicking off a (long) ingestion run. Dates
  // are OPTIONAL — leaving both blank lets the server read the range from the
  // imported schedule. We only validate when the user actually entered dates.
  const validateDates = (): boolean => {
    if (!startDate && !endDate) {
      setDateError('');
      return true;
    }
    if (!startDate || !endDate) {
      setDateError('Add both dates, or leave both blank to use the schedule.');
      return false;
    }
    if (endDate < startDate) {
      setDateError('The end date cannot be before the start date.');
      return false;
    }
    setDateError('');
    return true;
  };

  // Derive the confirm-ready UTC bounds from the inclusive dates the user sees,
  // but only when they entered them. Left blank, the bounds are undefined and
  // the server derives the tournament's range from the games. The API's all-day
  // end is EXCLUSIVE, so the last visible day maps to the following midnight.
  const buildResult = (ingestionId: string): EventStepResult => {
    const hasDates = Boolean(startDate && endDate);
    return {
      ingestionId,
      eventName: eventName.trim(),
      startDate,
      endDate,
      startsAtUtc: hasDates ? inclusiveDateToStartIso(startDate) : undefined,
      endsAtUtc: hasDates ? inclusiveDateToExclusiveEndIso(endDate) : undefined,
    };
  };

  // Auto-proceed once a run completes: after the (often long) extraction there's
  // nothing more for the coach to do on this step, so we report up immediately
  // instead of making them click through. Fires once per run (proceededRef); the
  // completed footer keeps a manual "continue" button as a fallback if the
  // confirm bridge errors and the wizard stays on this step.
  const proceededRef = useRef(false);
  useEffect(() => {
    if (state.phase !== 'completed' || !state.ingestionId || proceededRef.current) {
      return;
    }

    // A link run stops here for review instead. Proceeding runs the confirm
    // bridge, which creates the draft tournament and its games -- and a confirmed
    // ingestion can no longer be re-extracted, so auto-proceeding would close the
    // correction loop before the admin had seen a single extracted game.
    // A file run is unchanged: there is nothing to correct and nothing to re-read.
    if (runSourceRef.current === 'link') {
      return;
    }

    proceededRef.current = true;
    onCompleted(buildResult(state.ingestionId));
    // Intentionally keyed only to run completion, not to the callback / date
    // inputs (which are fixed by the time a run completes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.ingestionId]);

  // Validate every file up front so an unsupported type or an oversized file
  // fails instantly with a clear message rather than after a slow upload. All
  // selected files import into ONE schedule (e.g. several PDFs of a bracket).
  const submitFiles = (files: File[]) => {
    if (files.length === 0) return;
    for (const file of files) {
      const fileError = validateScheduleFile(file);
      if (fileError) {
        setSourceError(files.length === 1 ? fileError : `${file.name}: ${fileError}`);
        return;
      }
    }
    if (!validateDates()) return;
    setSourceError('');
    runSourceRef.current = source;
    // Seed a sensible name from the first file so the event isn't left unnamed.
    // Only when blank, so a name the coach already typed is never overwritten;
    // the field stays editable either way.
    if (!eventName.trim()) {
      const derived = deriveEventName(files[0].name);
      if (derived) setEventName(derived);
    }
    void runFiles(files);
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset the input so picking the same file(s) again re-triggers change.
    e.target.value = '';
    if (files.length > 0) submitFiles(files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (isRunning) return;
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) submitFiles(files);
  };

  const submitLink = () => {
    const trimmed = linkUrl.trim();
    if (!trimmed) {
      setSourceError('Paste a schedule link first.');
      return;
    }
    if (!validateDates()) return;
    setSourceError('');

    runSourceRef.current = 'link';
    // Rendered, always, on this path. The card only exists because the plain GET
    // returns an app shell for the sites it is for; asking for Http here would
    // reproduce the exact failure it was added to fix.
    void runUrl(trimmed, 'Rendered');
  };

  const handleSourceSelect = (next: ScheduleSource) => {
    setSource(next);
    setSourceError('');
    setDragActive(false);
    proceededRef.current = false;
    runSourceRef.current = null;
    reset();
  };

  const isRunning = state.phase === 'uploading' || state.phase === 'extracting';

  // When the extraction completes, surface a confirm-and-continue action rather
  // than auto-advancing, so the user stays in control of leaving Step 1.
  const completed = state.phase === 'completed';

  // Nothing loads before the form shows. An import in flight is no moment to start the tour.
  useTour(BUILD_ITINERARY_FEATURE, {
    ready: !isRunning,
    hasData: false,
    pageTourKey: BUILD_ITINERARY_TOURS.event,
    dataTourKey: null,
  });

  const handleContinue = () => {
    if (state.ingestionId) onCompleted(buildResult(state.ingestionId));
  };

  return (
    <div className="wiz-cols">
      <div className="wiz-card wiz-card--pad">
        <h2 className="wiz-step-title">Step 1: Add Event Schedule</h2>
        <p className="wiz-sub">
          Import a tournament, showcase, camp, or other recruiting event
          schedule.
        </p>

        <div className="wiz-foot wiz-foot--top">
          <span className="wiz-legend">
            {isSystemAdmin
              ? 'Upload a PDF or image of the schedule, or paste a link, to continue.'
              : 'Upload a PDF or image of the schedule to continue.'}
          </span>
          {completed ? (
            <button
              type="button"
              ref={resolvedActionRef}
              className="wiz-btn wiz-btn--primary"
              onClick={handleContinue}
              data-testid="wizard-next-players"
            >
              Next: Choose Players →
            </button>
          ) : (
            <button
              type="button"
              className="wiz-btn wiz-btn--primary"
              disabled
              title="Import a schedule to continue"
              data-testid="wizard-next-players-disabled"
            >
              Next: Choose Players →
            </button>
          )}
        </div>

        <div className="wiz-field" {...tourAnchor('build-itinerary.event.name')}>
          <label htmlFor="wizard-event-name">Event name</label>
          <input
            id="wizard-event-name"
            type="text"
            value={eventName}
            placeholder="e.g. Palm Beach Classic"
            onChange={(e) => setEventName(e.target.value)}
            data-testid="wizard-event-name"
          />
        </div>

        <div className="wiz-field">
          <span className="wiz-field__label">Schedule source</span>
        </div>
        <SourceChooser
          selected={source}
          onSelect={handleSourceSelect}
          allowLink={isSystemAdmin}
        />

        {source === 'link' ? (
          <div className="wiz-field">
            <label htmlFor="wizard-schedule-link">Schedule link</label>
            <input
              id="wizard-schedule-link"
              type="url"
              value={linkUrl}
              placeholder="https://..."
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isRunning) {
                  e.preventDefault();
                  submitLink();
                }
              }}
              disabled={isRunning}
              data-testid="wizard-schedule-link"
            />
            <button
              type="button"
              className="wiz-btn wiz-btn--primary"
              onClick={submitLink}
              disabled={isRunning}
              data-testid="wizard-read-link"
            >
              Read this link
            </button>
          </div>
        ) : (
        <div className="wiz-field">
          <span className="wiz-field__label">
            {source === 'pdf' ? 'Schedule PDF' : 'Schedule image'}
          </span>
          <button
            type="button"
            className={`wiz-dropzone${dragActive ? ' wiz-dropzone--drag' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isRunning) setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            disabled={isRunning}
            data-testid="wizard-dropzone"
          >
            <span className="wiz-dropzone__icon" aria-hidden="true">
              {source === 'pdf' ? '📄' : '🖼'}
            </span>
            <span className="wiz-dropzone__title">
              Drag &amp; drop your {source === 'pdf' ? 'PDFs' : 'images'} here
            </span>
            <span className="wiz-dropzone__sub">
              or <span className="wiz-dropzone__browse">browse files</span>
            </span>
            <span className="wiz-dropzone__hint">
              {source === 'pdf'
                ? 'One or more PDFs, up to 50 MB each'
                : 'One or more PNG/JPG images, up to 50 MB each'}
            </span>
          </button>
          <input
            ref={fileInputRef}
            id="wizard-file-input"
            type="file"
            accept={isFileSource ? ACCEPT_BY_SOURCE[source] : undefined}
            multiple
            onChange={handleFilePick}
            data-testid="wizard-file-input"
            hidden
          />
        </div>
        )}
        {sourceError && (
          <p
            className="wiz-error"
            role="alert"
            data-testid="wizard-source-error"
          >
            {sourceError}
          </p>
        )}

        <div className="wiz-field wiz-field--dates">
          <div>
            <label htmlFor="wizard-start-date">Start date (optional)</label>
            <input
              id="wizard-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="wizard-start-date"
            />
          </div>
          <div>
            <label htmlFor="wizard-end-date">End date (optional)</label>
            <input
              id="wizard-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="wizard-end-date"
            />
          </div>
        </div>
        <p className="wiz-sub" data-testid="wizard-dates-hint">
          Leave these blank and we'll read the dates from your schedule. Set them
          only to override.
        </p>
        {dateError && (
          <p className="wiz-error" role="alert" data-testid="wizard-date-error">
            {dateError}
          </p>
        )}

        <div className="wiz-note">
          <span className="wiz-note__icon" aria-hidden="true">
            i
          </span>
          <div>
            <b>Tip:</b> use the page that lists all games, times, fields, and
            matchups. Re-importing replaces this tournament's games.
          </div>
        </div>

        {isRunning && (
          <div className="wiz-progress" data-testid="wizard-progress">
            <span className="wiz-spinner" aria-hidden="true" />
            <span>
              {state.phase === 'uploading'
                ? 'Uploading your schedule...'
                : state.progressLabel ||
                  'Reading the games from your schedule...'}
            </span>
          </div>
        )}

        {state.phase === 'failed' && (
          <div className="wiz-error-box" data-testid="wizard-run-error">
            <p role="alert">{state.error}</p>
            <button
              type="button"
              ref={resolvedActionRef}
              className="wiz-btn"
              onClick={reset}
              data-testid="wizard-run-retry"
            >
              Try again
            </button>
          </div>
        )}

        {/*
          A link run lands here on zero games as well as on games found. Zero is the
          case most worth correcting -- the page rendered, so its text is retained and
          a re-prompt costs one model call -- and routing it to the generic "try a
          different source" box below would call reset() and throw that text away.
        */}
        {(completed || state.phase === 'empty') &&
          runSourceRef.current === 'link' &&
          state.ingestionId && (
          <LinkImportReview
            ingestionId={state.ingestionId}
            sourceUrl={linkUrl.trim()}
            onContinue={handleContinue}
            onStartOver={() => {
              runSourceRef.current = null;
              reset();
            }}
            continueRef={resolvedActionRef}
          />
        )}

        {state.phase === 'empty' && runSourceRef.current !== 'link' && (
          <div className="wiz-error-box" data-testid="wizard-run-empty">
            <p>
              We read that source but didn't find any games. Try a direct link
              to the schedule page, or upload a clearer PDF or screenshot.
            </p>
            <button
              type="button"
              ref={resolvedActionRef}
              className="wiz-btn"
              onClick={reset}
            >
              Try a different source
            </button>
          </div>
        )}

      </div>

      <WhatHappensNextRail />
    </div>
  );
}
